import React, { useState, useEffect } from 'react';
import { Store, ExternalLink, ChevronRight, Package, ShoppingBag, Zap, CheckCircle, LogOut, User, Users } from 'lucide-react';
import AddClientModal from './AddClientModal';
import UsersPage from './UsersPage';
import { tenantService } from '../services/api';

const ClientsPage = ({ onSelectClient, user, onLogout }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUsersPage, setShowUsersPage] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await tenantService.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMarketplaceName = (type) => {
    switch (type) {
      case 'LEROY_MERLIN': return 'Leroy Merlin';
      case 'MAKRO': return 'Makro';
      case 'TAKEALOT': return 'Takealot';
      default: return type;
    }
  };

  const WebsiteLink = ({ url }) => {
    if (!url) return null;
    const displayUrl = url.replace('https://', '').replace('http://', '');
    return React.createElement('a', {
      href: url,
      target: '_blank',
      rel: 'noopener noreferrer',
      style: styles.clientWebsite,
      onClick: (e) => e.stopPropagation()
    }, [
      displayUrl,
      React.createElement(ExternalLink, { key: 'icon', size: 12 })
    ]);
  };

  const renderClientCard = (client) => {
    const isConnected = client.woocommerceConnections?.[0]?.status === 'CONNECTED';
    const productCount = client._count?.stagedProducts || 0;
    const marketplaceCount = client.marketplaceConnections?.length || 0;

    return (
      <div
        key={client.id}
        style={styles.clientCard}
        onClick={() => onSelectClient(client)}
      >
        <div style={styles.cardHeader}>
          <div style={styles.clientLogo}>
            {client.logo ? (
              <img src={client.logo} alt={client.name} style={styles.logoImage} />
            ) : (
              <Store size={32} color="#8e8e93" />
            )}
          </div>
          {isConnected && (
            <div style={styles.connectedBadge}>
              <CheckCircle size={12} color="#22c55e" />
              <span>Connected</span>
            </div>
          )}
        </div>

        <div style={styles.cardBody}>
          <h3 style={styles.clientName}>{client.name}</h3>
          <WebsiteLink url={client.website} />
        </div>

        <div style={styles.cardStats}>
          <div style={styles.statItem}>
            <Package size={16} color="#6b7280" />
            <span style={styles.statValue}>{productCount}</span>
            <span style={styles.statLabel}>Products</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statItem}>
            <ShoppingBag size={16} color="#6b7280" />
            <span style={styles.statValue}>{marketplaceCount}</span>
            <span style={styles.statLabel}>Marketplaces</span>
          </div>
        </div>

        {marketplaceCount > 0 && (
          <div style={styles.cardMarketplaces}>
            {client.marketplaceConnections.map((mp) => (
              <span key={mp.id} style={styles.marketplaceTag}>
                {getMarketplaceName(mp.marketplaceType)}
              </span>
            ))}
          </div>
        )}

        <div style={styles.cardFooter}>
          <span style={styles.manageText}>Manage</span>
          <ChevronRight size={18} color="#3b82f6" />
        </div>
      </div>
    );
  };

  const UserMenu = () => (
    <div style={styles.userMenu}>
      <div style={styles.userInfo}>
        <div style={styles.userAvatar}>
          {user?.avatar ? (
            <img src={user.avatar} alt="" style={styles.userAvatarImg} />
          ) : (
            <User size={18} color="#fff" />
          )}
        </div>
        <div style={styles.userDetails}>
          <span style={styles.userName}>{user?.name || user?.email}</span>
          <span style={styles.userRole}>{user?.role?.replace('_', ' ')}</span>
        </div>
      </div>
      <button style={styles.logoutBtn} onClick={onLogout} title="Logout">
        <LogOut size={18} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.logoSection}>
              <div style={styles.logoIcon}>
                <Zap size={24} color="#fff" />
              </div>
              <div>
                <h1 style={styles.title}>SyncBridge</h1>
                <p style={styles.subtitle}>Marketplace Integration</p>
              </div>
            </div>
            <UserMenu />
          </div>
        </div>
        <div style={styles.main}>
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoSection}>
            <div style={styles.logoIcon}>
              <Zap size={24} color="#fff" />
            </div>
            <div>
              <h1 style={styles.title}>SyncBridge</h1>
              <p style={styles.subtitle}>Marketplace Integration</p>
            </div>
          </div>
          <div style={styles.headerRight}>
            {user?.role === 'SUPER_ADMIN' && (
            <button style={styles.usersBtn} onClick={() => setShowUsersPage(true)}>
              <Users size={18} />
              Users
            </button>
          )}
          <button style={styles.addBtn} onClick={() => setShowAddModal(true)}>
              + Add Client
            </button>
            <UserMenu />
          </div>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Clients</h2>
          <p style={styles.sectionSubtitle}>{clients.length} connected</p>
        </div>

        {clients.length === 0 ? (
          <div style={styles.emptyState}>
            <Store size={48} color="#d1d5db" />
            <h3 style={styles.emptyTitle}>No clients yet</h3>
            <p style={styles.emptySubtitle}>Add your first client to get started</p>
            <button style={styles.addBtnLarge} onClick={() => setShowAddModal(true)}>
              + Add Client
            </button>
          </div>
        ) : (
          <div style={styles.clientsGrid}>
            {clients.map((client) => renderClientCard(client))}
          </div>
        )}
      </div>

      {showUsersPage && (
        <UsersPage onClose={() => setShowUsersPage(false)} />
      )}

      {showAddModal && (
        <AddClientModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchClients();
          }}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
  },
  header: {
    backgroundColor: '#111827',
    padding: '24px 0',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImg: {
    width: '44px',
    height: '44px',
    objectFit: 'contain',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#fff',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  usersBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  addBtn: {
    padding: '10px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  addBtnLarge: {
    padding: '12px 24px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '16px',
  },
  userMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#374151',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  userAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#fff',
  },
  userRole: {
    fontSize: '11px',
    color: '#9ca3af',
    textTransform: 'capitalize',
  },
  logoutBtn: {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: '#9ca3af',
    cursor: 'pointer',
  },
  main: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px',
  },
  sectionHeader: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '4px 0 0 0',
  },
  loadingText: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '60px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 40px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '16px 0 8px 0',
  },
  emptySubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  clientsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '20px',
  },
  clientCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    border: '1px solid #e5e7eb',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  clientLogo: {
    width: '56px',
    height: '56px',
    borderRadius: '12px',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  connectedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: '#f0fdf4',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#16a34a',
  },
  cardBody: {
    marginBottom: '16px',
  },
  clientName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0',
  },
  clientWebsite: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    color: '#3b82f6',
    textDecoration: 'none',
  },
  cardStats: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderTop: '1px solid #f3f4f6',
    borderBottom: '1px solid #f3f4f6',
    marginBottom: '12px',
  },
  statItem: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  statValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
  },
  statLabel: {
    fontSize: '12px',
    color: '#6b7280',
  },
  statDivider: {
    width: '1px',
    height: '24px',
    backgroundColor: '#e5e7eb',
  },
  cardMarketplaces: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '12px',
  },
  marketplaceTag: {
    padding: '4px 10px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#374151',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '12px',
    borderTop: '1px solid #f3f4f6',
  },
  manageText: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#3b82f6',
  },
};

export default ClientsPage;



